// components/BreadcrumbsNav.jsx
// Lightweight, reusable breadcrumb for pages (e.g., "Availability / Availability Template").
// Usage:
// <BreadcrumbsNav items={[
//   { label: t('planning.availability') || 'Availability', to: '/availability' },
//   { label: t('planning.create_template') || 'Availability Template' },
// ]} />

import React from 'react';
import { Box, Breadcrumbs, Link, Typography, useTheme } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

/**
 * items: Array<{ label: string, to?: string, onClick?: () => void }>
 * - If `to` or `onClick` is provided, item renders as a link; otherwise it renders as the current page label.
 */
export default function BreadcrumbsNav({ items = [], sx }) {
  const theme = useTheme();

  return (
    <Box sx={{ mb: 1, marginBottom: "15px", ...sx }}>
      <Breadcrumbs
        aria-label="breadcrumb"
        separator="/"
        sx={{
          '& .MuiBreadcrumbs-separator': { mx: 1, opacity: 0.6 },
          color: 'text.secondary',
          fontSize: 14,
        }}
      >
        {items.map((item, idx) => {
          const isLast = idx === items.length - 1;
          if (isLast || (!item.to && !item.onClick)) {
            return (
              <Typography
                key={item.label + idx}
                variant="body2"
                color="text.primary"
                sx={{ fontWeight: 600 }}
              >
                {item.label}
              </Typography>
            );
          }
          return (
            <Link
              key={item.label + idx}
              component={item.to ? RouterLink : 'button'}
              to={item.to}
              onClick={item.onClick}
              underline="hover"
              color="inherit"
              variant="body2"
              sx={{ cursor: 'pointer' }}
            >
              {item.label}
            </Link>
          );
        })}
      </Breadcrumbs>
    </Box>
  );
}
